<template>
  <main class="main">
    <section class="product" v-if="object">
      <Breadcrumbs :breadcrumbs="breadcrumbs" />

      <div class="container">
        <div class="product__wrapper">
          <div class="product-card">
            <div class="product-card__wrapper">

              <!-- Слайдер фото объекта -->
              <div class="product-card__slider product-card-slider">
                <div class="product-card-slider__swiper swiper">
                  <div class="product-card-slider__wrapper swiper-wrapper">
                    <div class="product-card-slider__slide swiper-slide">
                      <Card :slider="slider" :showNavigation="slider.length > 1" />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Контент -->
              <div class="product-card__content">
                <h1 class="product-card__title title">{{ object.name }}</h1>

                <div class="re-obj__meta">
                  <span v-if="typeLabel" class="re-obj__type">{{ typeLabel }}</span>
                  <NuxtLink
                    v-if="object.project"
                    :to="`/real-estate/project/${object.project.id}`"
                    class="re-obj__project-link"
                  >
                    {{ object.project.name }}
                  </NuxtLink>
                </div>

                <div v-if="object.tagline" class="re-obj__tagline">{{ object.tagline }}</div>

                <div v-if="priceText" class="re-obj__price">{{ priceText }}</div>

                <!-- Характеристики -->
                <div v-if="hasSpecs" class="re-obj__specs">
                  <div v-if="object.areaSqm" class="re-obj__spec">
                    <span class="re-obj__spec-label">Площадь</span>
                    <span class="re-obj__spec-value">{{ object.areaSqm }} м²</span>
                  </div>
                  <div v-if="object.bedrooms != null" class="re-obj__spec">
                    <span class="re-obj__spec-label">Спальни</span>
                    <span class="re-obj__spec-value">{{ object.bedrooms }}</span>
                  </div>
                  <div v-if="object.bathrooms != null" class="re-obj__spec">
                    <span class="re-obj__spec-label">Ванные</span>
                    <span class="re-obj__spec-value">{{ object.bathrooms }}</span>
                  </div>
                  <div v-if="object.floor != null" class="re-obj__spec">
                    <span class="re-obj__spec-label">Этаж</span>
                    <span class="re-obj__spec-value">
                      {{ object.floor }}{{ object.totalFloors ? ' / ' + object.totalFloors : '' }}
                    </span>
                  </div>
                  <div v-if="completionText" class="re-obj__spec">
                    <span class="re-obj__spec-label">Срок сдачи</span>
                    <span class="re-obj__spec-value">{{ completionText }}</span>
                  </div>
                </div>

                <button class="re-cta-btn" @click="showInquiry = true">
                  {{ object.ctaTitle || 'Связаться с застройщиком' }}
                </button>

                <div class="product-card__info-wrapper desktop">
                  <div class="product-card__info info" v-html="object.description" />
                </div>
              </div>
            </div>

            <!-- Описание (мобилка) -->
            <div class="product-card__info-wrapper mobile">
              <div class="product-card__info info" v-html="object.description" />
            </div>

            <!-- Редакционный текст (полная ширина) -->
            <div v-if="object.editorialText" class="re-obj__editorial">
              <div class="info" v-html="object.editorialText" />
            </div>
          </div>

          <!-- Другие объекты проекта -->
          <section v-if="otherObjects.length" class="vibe-realize">
            <div class="vibe-realize__block">
              <h3 class="vibe-realize__subtitle">
                Другие объекты{{ object.project ? ' в проекте ' + object.project.name : '' }}
              </h3>
              <div class="vibe-re__grid">
                <NuxtLink
                  v-for="obj in otherObjects"
                  :key="obj.id"
                  :to="`/real-estate/${obj.id}`"
                  class="vibe-re__card"
                >
                  <div class="vibe-re__image">
                    <img
                      v-if="obj.mainImage"
                      :src="fixUrl(obj.mainImage.path)"
                      :alt="obj.name"
                    />
                    <div v-else class="vibe-re__placeholder">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2">
                        <path d="M3 21V7l9-4 9 4v14M9 21v-6h6v6M12 3v18"/>
                      </svg>
                    </div>
                  </div>
                  <div class="vibe-re__info">
                    <span class="vibe-re__label">{{ typeLabels[obj.propertyType] || obj.propertyType }}</span>
                    <span class="vibe-re__name">{{ obj.name }}</span>
                    <span v-if="obj.priceFrom || obj.price" class="vibe-re__price">
                      от {{ formatPrice(obj.priceFrom || obj.price, obj.currency) }}
                    </span>
                  </div>
                </NuxtLink>
              </div>
            </div>
          </section>

        </div>
      </div>
    </section>

    <Spinner v-else />

    <RealEstateInquiryModal
      v-if="showInquiry"
      :title="object?.ctaTitle || 'Связаться с застройщиком'"
      :objectId="object?.id"
      @close="showInquiry = false"
    />
  </main>
</template>

<script setup lang="ts">
import Card from '~/components/UI/Card/Card.vue'
import Breadcrumbs from '~/components/Section/Breadcrumbs.vue'
import Spinner from '~/components/animate/SpinnerComponent.vue'
import RealEstateInquiryModal from '~/components/Modals/RealEstateInquiry.vue'
import { RealEstateObject as RealEstateObjectApi } from '~/api/RealEstate'

const showInquiry = ref(false)

const route = useRoute()
const id = Number(route.params.id)

let object: any = null
let otherObjects: any[] = []

try {
  object = await RealEstateObjectApi.getOne(id)
} catch {}

if (object?.project?.id) {
  try {
    const res = await RealEstateObjectApi.getByProject(object.project.id)
    otherObjects = (res?.objects ?? []).filter((o: any) => o.id !== id)
  } catch {}
}

const fixUrl = (url?: string) => {
  if (!url) return ''
  const s = (url as string).replace(/\\/g, '/')
  if (s.startsWith('http://') || s.startsWith('https://')) return s
  const config = useRuntimeConfig()
  return (config.public.apiDomain as string).replace(/\/api$/, '') + '/storage' + s
}

const slider = (object?.files ?? [])
  .filter((f: any) => f.isImage)
  .map((f: any) => ({ id: f.id, src: fixUrl(f.path) }))

const typeLabels: Record<string, string> = {
  apartment:   'Квартира',
  apartments:  'Апартаменты',
  studio:      'Студия',
  penthouse:   'Пентхаус',
  loft:        'Лофт',
  villa:       'Вилла',
  land:        'Земельный участок',
  commercial:  'Коммерция',
  hospitality: 'Гостиничный бизнес',
}
const typeLabel = typeLabels[object?.propertyType] || object?.propertyType || ''

const currencySymbols: Record<string, string> = {
  RUB: '₽', AED: 'AED', EUR: '€', USD: '$',
}
const formatPrice = (amount: number, currency: string) =>
  new Intl.NumberFormat('ru-RU').format(amount) + ' ' + (currencySymbols[currency] ?? currency)

const priceVal = object?.price || object?.priceFrom
const priceText = priceVal
  ? (object?.priceFrom ? 'от ' : '') + formatPrice(priceVal, object.currency)
  : ''

const completionText = object?.completionQuarter
  || (object?.completionDate ? object.completionDate.slice(0, 7).replace('-', '.') : '')

const hasSpecs = object?.areaSqm || object?.bedrooms != null || object?.bathrooms != null
  || object?.floor != null || completionText

const breadcrumbs = [
  { title: 'Главная', url: '/' },
  { title: 'Недвижимость', url: '/real-estate' },
  ...(object?.project
    ? [{ title: object.project.name, url: `/real-estate/project/${object.project.id}` }]
    : []),
  { title: object?.name ?? 'Объект', url: null },
]

useHead({
  title: object?.metaTitle || object?.name || 'Объект',
  meta: [
    { name: 'description', content: object?.metaDescription || object?.tagline || object?.description || '' },
  ],
})
</script>

<style scoped>
.re-obj__editorial {
  margin-top: 4rem;
  padding-top: 4rem;
  border-top: 1px solid #f0f0f0;
  max-width: 70%;
  margin-left: auto;
  margin-right: auto;
}
@media (max-width: 900px) {
  .re-obj__editorial { max-width: 100%; }
}

.re-obj__meta {
  display: flex;
  align-items: center;
  gap: 1.2rem;
  margin-bottom: 1.6rem;
  flex-wrap: wrap;
}
.re-obj__type {
  display: inline-block;
  padding: 3px 10px;
  border: 1px solid #ccc;
  border-radius: 2px;
  font-size: 1rem;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: #888;
}
.re-obj__project-link {
  font-size: 1.2rem;
  color: #999;
  text-decoration: none;
  border-bottom: 1px solid #ddd;
  transition: color 0.2s, border-color 0.2s;
}
.re-obj__project-link:hover {
  color: #333;
  border-color: #999;
}

.re-obj__tagline {
  font-size: 1.5rem;
  color: #555;
  font-weight: 300;
  margin-bottom: 2rem;
  line-height: 1.5;
}

.re-cta-btn {
  display: inline-block;
  margin-top: 2rem;
  margin-bottom: 2rem;
  padding: 1.4rem 3rem;
  background: #1B1918;
  color: #fff;
  border: none;
  border-radius: 2px;
  font-size: 1.3rem;
  letter-spacing: 0.06em;
  cursor: pointer;
  transition: opacity 0.2s;
}
.re-cta-btn:hover { opacity: 0.8; }

.re-obj__price {
  font-size: 2.2rem;
  font-weight: 500;
  color: #1a1a1a;
  margin-bottom: 2.4rem;
  letter-spacing: -0.02em;
}

.re-obj__specs {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  margin-bottom: 2.4rem;
  padding: 1.6rem 0;
  border-top: 1px solid #f0f0f0;
  border-bottom: 1px solid #f0f0f0;
}
.re-obj__spec {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}
.re-obj__spec-label {
  font-size: 1.2rem;
  color: #999;
}
.re-obj__spec-value {
  font-size: 1.3rem;
  font-weight: 500;
  color: #1a1a1a;
}

/* reuse grid/cards from project page */
.vibe-realize {
  margin-top: 6rem;
}
.vibe-realize__block {
  margin-bottom: 4rem;
}
.vibe-realize__subtitle {
  font-size: 1.1rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #888;
  margin: 0 0 2rem;
}
.vibe-re__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}
@media (max-width: 900px) {
  .vibe-re__grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 560px) {
  .vibe-re__grid { grid-template-columns: 1fr; }
}
.vibe-re__card {
  display: block;
  text-decoration: none;
  color: inherit;
  border-radius: 12px;
  overflow: hidden;
  background: #f7f7f5;
  transition: transform 0.2s ease;
}
.vibe-re__card:hover { transform: translateY(-3px); }
.vibe-re__image {
  width: 100%;
  aspect-ratio: 4/3;
  overflow: hidden;
  background: #ebebeb;
}
.vibe-re__image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.vibe-re__placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #bbb;
}
.vibe-re__placeholder svg { width: 48px; height: 48px; }
.vibe-re__info {
  padding: 1.4rem 1.6rem 1.8rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.vibe-re__label {
  font-size: 1.1rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #999;
}
.vibe-re__name {
  font-size: 1.6rem;
  font-weight: 500;
  color: #1a1a1a;
  line-height: 1.3;
}
.vibe-re__price {
  font-size: 1.3rem;
  color: #555;
  margin-top: 0.2rem;
}
</style>
